/**
 * Data Categorization Constants
 *
 * Centralized configuration for teams, statuses, and other constants
 *
 * @author Development Team
 * @version 1.0
 */

/**
 * Team Definitions
 * 
 * To add a new team:
 * 1. Add a new entry to TEAMS array with label, value, and severity
 * 2. The team will automatically appear in all dropdowns and filters
 * 
 * Severity options (for color coding):
 * - 'info' (blue)
 * - 'success' (green)
 * - 'warning' (orange)
 * - 'danger' (red)
 * - 'secondary' (gray)
 */
export const TEAMS = [];

/**
 * Get team options for dropdown (includes 'All Teams' option)
 * @param {Array} teams - Array of team objects from API
 * @param {boolean} includeAll - Whether to include "All Teams" option
 * @returns {Array} Array of team options
 */
export const getTeamOptions = (teams = [], includeAll = false) => {
  const options = teams.map(team => ({
    label: team.label,
    value: team.value
  }));
  
  if (includeAll) {
    return [{ label: 'All Teams', value: '' }, ...options];
  }
  
  return options;
};

/**
 * Get team severity (color) by team value
 * @param {string} teamValue - Team value
 * @param {Array} teams - Array of team objects from API
 * @returns {string} Severity level
 */
export const getTeamSeverity = (teamValue, teams = []) => {
  const team = teams.find(t => t.value === teamValue);
  return team ? team.severity : 'secondary';
};

/**
 * Status options for rules and categories
 */
export const STATUS_OPTIONS = [
  { label: 'All Status', value: null },
  { label: 'Enabled', value: true },
  { label: 'Disabled', value: false }
];

/**
 * File upload constraints
 */
export const FILE_UPLOAD = {

  MAX_SIZE: 104857600, // 100MB in bytes
  ACCEPTED_FORMATS: ['.csv', '.xls', '.xlsx'],
  ACCEPTED_MIME_TYPES: [
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ]
};

/**
 * Batch processing statuses
 */
export const BATCH_STATUS = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED'
};

/**
 * Get batch status severity
 * @param {string} status - Batch status
 * @returns {string} Severity level
 */
export const getBatchStatusSeverity = (status) => {
  const statusMap = {
    [BATCH_STATUS.COMPLETED]: 'success',
    [BATCH_STATUS.PROCESSING]: 'info',
    [BATCH_STATUS.FAILED]: 'danger',
    [BATCH_STATUS.CANCELLED]: 'warning',
    [BATCH_STATUS.PENDING]: 'secondary'
  };
  return statusMap[status] || 'secondary';
};

/**
 * Pagination defaults
 */
export const PAGINATION = {
  DEFAULT_PAGE: 0,
  DEFAULT_SIZE: 20,
  SIZE_OPTIONS: [10, 20, 50, 100]
};
